import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';

// Import shared components
import * as geocoderMapbox from '../../../../components/LocationAutocompleteInput/GeocoderMapbox';
import * as geocoderGoogleMaps from '../../../../components/LocationAutocompleteInput/GeocoderGoogleMaps';

// Import modules from this directory
import EditListingAlertForm from './EditListingAlertForm';

import { stringifyDateToISO8601, parseDateFromISO8601 } from '../../../../util/dates';
import { types as sdkTypes } from '../../../../util/sdkLoader';

import doodle from './doodle2.png';
import css from './EditListingAlertPanel.module.css';

const { Money } = sdkTypes;

// ---------------------------------------------------------------------------
// Presentational line icons for the surrounding marketing layout. They inherit
// color via `currentColor` and are hidden from assistive tech.
// ---------------------------------------------------------------------------
const svgProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

const IconClipboard = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" {...svgProps}>
    <rect x="5" y="4" width="14" height="17" rx="2" />
    <path d="M9 4a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 4v1H9V4zM8.5 10h7M8.5 14h7M8.5 18h4" />
  </svg>
);

const IconPaperPlane = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" {...svgProps}>
    <path d="M21 3L3 10.5l6.5 2.5L12 20l3-8 6-9zM9.5 13L21 3" />
  </svg>
);

const IconEnvelope = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" {...svgProps}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M4 7l8 6 8-6" />
  </svg>
);

const IconLock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...svgProps}>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

const IconUsers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...svgProps}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
  </svg>
);

const IconHeartLine = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgProps}>
    <path d="M12 20s-7-4.35-9.33-9C1.34 8.4 2.5 5 5.6 5 7.6 5 9 6.5 12 9c3-2.5 4.4-4 6.4-4 3.1 0 4.26 3.4 2.93 6C19 15.65 12 20 12 20z" />
  </svg>
);

const IconShieldHeart = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...svgProps}>
    <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" />
    <path d="M9 11c.8-1 2-.6 3 .5 1-1.1 2.2-1.5 3-.5.8 1 .3 2.2-3 4-3.3-1.8-3.8-3-3-4z" />
  </svg>
);

const IconClockLine = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" {...svgProps}>
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

// Hand-drawn style heart used as an accent next to the form title.
const IconHeartDoodle = () => (
  <svg width="52" height="46" viewBox="0 0 52 46" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M27 42C16 35 4 27 4 16 4 9 9 4 16 5c5 .8 9 5 11 9 2-4 6-8.5 11-9 7-1 12 4 12 11 0 11-12 19-23 26z" />
  </svg>
);

const getGeocoder = mapProvider => {
  const variant = mapProvider === 'googleMaps' ? geocoderGoogleMaps : geocoderMapbox;
  return new variant.default();
};

// Forward-geocode the selected location label (e.g. "London") into a place with
// coordinates, using the same map provider (Mapbox) the rest of the app uses.
// The alert stores the resulting geolocation so the matching job can match it
// against listings by proximity, just like it did before the dropdown change.
const geocodeLocationLabel = (config, label) => {
  if (!label) {
    return Promise.resolve(null);
  }
  const geocoder = getGeocoder(config?.maps?.mapProvider);
  const countryLimit = config?.maps?.search?.countryLimit;
  const locale = config?.localization?.locale;
  return geocoder.getPlacePredictions(label, countryLimit, locale).then(({ predictions }) => {
    const first = predictions?.[0];
    return first ? geocoder.getPlaceDetails(first) : null;
  });
};

const getInitialValues = props => {
  const { listing, marketplaceCurrency } = props;
  const { publicData } = listing?.attributes || {};
  const {
    bookingDates,
    flexibleDates = [],
    guests = 1,
    bedrooms = 1,
    bathrooms = 1,
    maxPrice = 0,
    perfectStay,
  } = publicData || {};
  const location = publicData?.location || {};
  const { building, key: locationKey } = location;

  const maxPriceValue = maxPrice && new Money(maxPrice, marketplaceCurrency);

  const startDate = bookingDates?.startDate ? parseDateFromISO8601(bookingDates.startDate) : null;

  const endDate = bookingDates?.endDate ? parseDateFromISO8601(bookingDates.endDate) : null;

  const bookingDatesValue = { startDate, endDate };

  return {
    building,
    // The location field is now a dropdown whose value is the stored option key
    // (matching the "property_location" user field options).
    location: locationKey || '',
    bookingDates: bookingDatesValue,
    flexibleDates,
    guests,
    bedrooms,
    bathrooms,
    maxPrice: maxPriceValue,
    perfectStay,
  };
};

// The location dropdown reuses the options of the "property_location" user field
// (configured via hosted user fields), so an alert's location matches the same
// set of values users can pick for their properties.
const getLocationOptions = config => {
  const userFields = config?.user?.userFields || [];
  const propertyLocationField = userFields.find(f => f.key === 'property_location');
  return propertyLocationField?.enumOptions || [];
};

/**
 * The EditListingAlertPanel component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.ownListing} props.listing - The listing object
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.ready - Whether the form is ready
 * @param {Function} props.onSubmit - The submit function
 * @param {string} props.submitButtonText - The submit button text
 * @param {boolean} props.redirectToListing - Whether the panel is updated
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {Object} props.errors - The errors object
 * @returns {JSX.Element}
 */
const EditListingAlertPanel = props => {
  // State is needed since LocationAutocompleteInput doesn't have internal state
  // and therefore re-rendering would overwrite the values during XHR call.
  const [state, setState] = useState({ initialValues: getInitialValues(props) });
  const {
    className,
    rootClassName,
    disabled,
    ready,
    onSubmit,
    submitButtonText,
    redirectToListing,
    updateInProgress,
    errors,
    listingMinimumPriceSubUnits,
    marketplaceCurrency,
    config,
    onListingTypeChange,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const listingTypes = config.listing.listingTypes;
  const alertListingType = listingTypes.find(config => config.listingType === 'alert');
  const transactionProcessAlias = alertListingType?.transactionType?.alias;
  const locationOptions = getLocationOptions(config);

  // The alert panel hardcodes the listing type instead of letting the user pick it,
  // so we must explicitly inform the wizard of the selected type (as the details panel
  // does via onListingTypeChange). Without this, the wizard can't resolve the alert's
  // listingTypeConfig at publish time, requirePayoutDetails defaults to true, and the
  // payout details modal is shown even though this inquiry type has payoutDetails: false.
  useEffect(() => {
    if (alertListingType) {
      onListingTypeChange?.(alertListingType);
    }
  }, [alertListingType?.listingType]);

  const howSteps = [
    { icon: <IconClipboard />, key: 'howStep1' },
    { icon: <IconPaperPlane />, key: 'howStep2' },
    { icon: <IconEnvelope />, key: 'howStep3' },
  ];

  const whyItems = [
    { icon: <IconLock />, id: 'EditListingAlertPanel.whyItem1' },
    { icon: <IconUsers />, id: 'EditListingAlertPanel.whyItem2' },
    { icon: <IconHeartLine />, id: 'EditListingAlertPanel.whyItem3' },
    { icon: <IconShieldHeart />, id: 'EditListingAlertPanel.whyItem4' },
  ];

  const nextItems = [
    'EditListingAlertPanel.nextItem1',
    'EditListingAlertPanel.nextItem2',
    'EditListingAlertPanel.nextItem3',
    'EditListingAlertPanel.nextItem4',
  ];

  return (
    <div className={classes}>
      {/* Three-step "how it works" explainer above the form. */}
      <ol className={css.howSteps}>
        {howSteps.map((step, index) => (
          <React.Fragment key={step.key}>
            {index > 0 ? <li className={css.howConnector} aria-hidden="true" /> : null}
            <li className={css.howStep}>
              <div className={css.howIcon}>
                {step.icon}
                <span className={css.howNumber}>{index + 1}</span>
              </div>
              <h3 className={css.howStepTitle}>
                <FormattedMessage id={`EditListingAlertPanel.${step.key}Title`} />
              </h3>
              <p className={css.howStepText}>
                <FormattedMessage id={`EditListingAlertPanel.${step.key}Text`} />
              </p>
            </li>
          </React.Fragment>
        ))}
      </ol>

      {/* Left column on desktop: value proposition + what-happens-next cards.
          On mobile the cards stack around the form (why → form → what's next),
          which is why they are siblings of the form card rather than nested in
          a shared sidebar element. */}
      <div className={css.columns}>
        <section className={classNames(css.sideCard, css.whyCard)}>
          <h2 className={css.sideCardTitle}>
            <FormattedMessage id="EditListingAlertPanel.whyTitle" />
          </h2>
          <ul className={css.whyList}>
            {whyItems.map(item => (
              <li key={item.id} className={css.whyItem}>
                <span className={css.whyIcon}>{item.icon}</span>
                <span>
                  <FormattedMessage id={item.id} />
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className={classNames(css.sideCard, css.nextCard)}>
          <h2 className={css.sideCardTitle}>
            <FormattedMessage id="EditListingAlertPanel.nextTitle" />
          </h2>
          <ul className={css.nextList}>
            {nextItems.map(id => (
              <li key={id} className={css.nextItem}>
                <span className={css.nextIcon}>
                  <IconCheck />
                </span>
                <span>
                  <FormattedMessage id={id} />
                </span>
              </li>
            ))}
          </ul>
          <img className={css.houseDoodle} src={doodle} alt="" aria-hidden="true" />
        </section>

        {/* Right on desktop, between the two cards on mobile: the request form. */}
        <div className={css.formCard}>
          <div className={css.formHeader}>
            <div className={css.formHeaderText}>
              <h2 className={css.formTitle}>
                <FormattedMessage id="EditListingAlertPanel.formTitle" />
              </h2>
              <p className={css.formSubtitle}>
                <FormattedMessage id="EditListingAlertPanel.formSubtitle" />
              </p>
            </div>
            <span className={css.heartDoodle}>
              <IconHeartDoodle />
            </span>
          </div>

          <EditListingAlertForm
            className={css.form}
        initialValues={state.initialValues}
        onSubmit={async values => {
          const {
            building = '',
            location,
            bookingDates,
            flexibleDates = [],
            guests,
            bedrooms,
            bathrooms,
            maxPrice,
            perfectStay,
          } = values;
          // `location` is the selected option key from the property_location
          // dropdown. Store both the key and its label (for display), and
          // geocode the label into coordinates for proximity matching.
          const selectedOption = locationOptions.find(o => o.option === location);
          const address = selectedOption?.label || location;
          const place = await geocodeLocationLabel(config, address).catch(() => null);
          const origin = place?.origin || null;

          // New values for listing attributes
          const updateValues = {
            title: 'Alert',
            ...(origin ? { geolocation: origin } : {}),
            publicData: {
              location: {
                key: location,
                address,
              },
              bookingDates:
                bookingDates?.startDate && bookingDates?.endDate
                  ? {
                      startDate: stringifyDateToISO8601(bookingDates.startDate),
                      endDate: stringifyDateToISO8601(bookingDates.endDate),
                    }
                  : {},
              flexibleDates,
              guests,
              bedrooms,
              bathrooms,
              maxPrice: maxPrice?.amount,
              perfectStay,
              listingType: 'alert',
              transactionProcessAlias,
              unitType: 'inquiry',
            },
          };

          // Save the initialValues to state so re-rendering during the XHR call
          // doesn't overwrite the values.
          setState({
            initialValues: {
              building,
              location,
              flexibleDates,
              guests,
              bedrooms,
              bathrooms,
            },
          });
          onSubmit(updateValues);
        }}
        saveActionMsg={submitButtonText}
        disabled={disabled}
        ready={ready}
        updated={redirectToListing}
        updateInProgress={updateInProgress}
        fetchErrors={errors}
        listingMinimumPriceSubUnits={listingMinimumPriceSubUnits}
        marketplaceCurrency={marketplaceCurrency}
        locationOptions={locationOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default EditListingAlertPanel;
